import { createContext, ReactNode, useContext, useState } from "react";

interface MobileAppLayoutContextType {
  // Scroll state
  isScrolled: boolean;
  setScrolled: (scrolled: boolean) => void;

  // Layout dimensions
  navBarHeight: number;
  tabBarHeight: number;
  setNavBarHeight: (height: number) => void;
  setTabBarHeight: (height: number) => void;
}

const MobileAppLayoutContext = createContext<MobileAppLayoutContextType | undefined>(undefined);

export const MobileAppLayoutProvider = ({ children }: { children: ReactNode }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [navBarHeight, setNavBarHeight] = useState(0);
  const [tabBarHeight, setTabBarHeight] = useState(0);

  const setScrolled = (scrolled: boolean) => {
    setIsScrolled(scrolled);
  };

  return (
    <MobileAppLayoutContext.Provider
      value={{
        isScrolled,
        setScrolled,
        navBarHeight,
        tabBarHeight,
        setNavBarHeight,
        setTabBarHeight,
      }}
    >
      {children}
    </MobileAppLayoutContext.Provider>
  );
};

export const useMobileAppLayout = () => {
  const context = useContext(MobileAppLayoutContext);
  if (context === undefined) {
    throw new Error("useMobileAppLayout must be used within a MobileAppLayoutProvider");
  }
  return context;
};

// Safe hook that doesn't throw error if context is not available
export const useMobileAppLayoutSafe = () => {
  const context = useContext(MobileAppLayoutContext);
  return context; // Returns undefined if not within provider
};

// Convenience hooks for specific values
export const useScrolled = () => {
  const { isScrolled } = useMobileAppLayout();
  return isScrolled;
};

export const useNavBarHeight = () => {
  const { navBarHeight } = useMobileAppLayout();
  return navBarHeight;
};

export const useTabBarHeight = () => {
  const { tabBarHeight } = useMobileAppLayout();
  return tabBarHeight;
};
